import React, { useState } from 'react';
import { AuthPermissions } from 'skipify-types';

import { PaylinkCustomerInfoResponse } from '../../../store/modules/paylink/types';
import CustomerModal from '../../../pages/Paylinks/components/CustomerModal';
import { useCustomerUpdate, useCustomerDelete } from '../../../pages/Paylinks/hooks';
import { ActionDialogModal } from '../../../pages/Paylinks/components/ActionDialog/modal';
import { paylinkPaths, cardOnFilePaths } from '../../../util/constants';
import { useMerchant } from '../../../contexts/MerchantContext';

import { ActionsPopover } from '../../ActionsPopover';
import SendPaylinkModal from '../../../pages/Paylinks/SendPaylinkForm/modal';
import { CreateTransactionModal } from '../../../pages/CardOnFile/components/CreateTransaction/CreateTransactionModal';
import { useMerchantAwareNavigate } from '../../../hooks/useMerchantAwareNavigate';

interface Props {
  customer: PaylinkCustomerInfoResponse;
}

export const CustomerActionsPopover = ({ customer }: Props) => {
  const navigate = useMerchantAwareNavigate();
  const { canAccess, hasCardOnFileAccess } = useMerchant();

  const { onCustomerUpdate, serverValidationError, clearServerValidationError } = useCustomerUpdate(
    { customerId: customer.payLinkCustomerId },
  );

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isSendPaylinkModalOpen, setIsSendPaylinkModalOpen] = useState(false);
  const [isCreateTransactionModalOpen, setIsCreateTransactionModalOpen] = useState(false);

  const { onCustomerDelete, isLoading: isDeleting } = useCustomerDelete(customer.payLinkCustomerId);

  const viewPastPaylinkParams = new URLSearchParams(`customerIds=${customer.payLinkCustomerId}`);
  const viewPastTransactionsParams = new URLSearchParams(
    `customerIds=${customer.payLinkCustomerId}`,
  );
  const customerHasPaymentInfo = !!customer.lastFour && !!customer.networkType;

  return (
    <>
      <CustomerModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          clearServerValidationError();
        }}
        onSubmit={onCustomerUpdate}
        serverValidationError={serverValidationError}
        type="Edit"
        customer={customer}
      />
      <ActionDialogModal
        isOpen={isDeleteDialogOpen}
        onProceed={onCustomerDelete}
        isSubmitting={isDeleting}
        onClose={() => setIsDeleteDialogOpen(false)}
        content={{
          heading: 'Are you sure you want to delete this Customer?',
          cancelButtonText: 'Cancel',
          proceedButtonText: 'Delete this Customer',
        }}
      />
      <CreateTransactionModal
        isOpen={isCreateTransactionModalOpen}
        onClose={() => setIsCreateTransactionModalOpen(false)}
        preSelectedCustomer={customer}
      />
      <SendPaylinkModal
        isOpen={isSendPaylinkModalOpen}
        onClose={() => setIsSendPaylinkModalOpen(false)}
        preSelectedCustomer={customer}
      />
      <ActionsPopover
        ariaLabel="Customer Actions"
        actions={[
          {
            label: 'Create card on file transaction',
            onClick: () => setIsCreateTransactionModalOpen(true),
            disabled:
              (!canAccess(AuthPermissions.CHARGE_MIT) &&
                !canAccess(AuthPermissions.CHARGE_OWN_MIT)) ||
              !customerHasPaymentInfo,
            shouldRender: hasCardOnFileAccess(),
          },
          {
            label: 'View card on file transactions',
            onClick: () =>
              navigate(`${cardOnFilePaths.VIEW_ALL}?${viewPastTransactionsParams.toString()}`),
            disabled:
              (!canAccess(AuthPermissions.READ_MIT) && !canAccess(AuthPermissions.READ_OWN_MIT)) ||
              !customerHasPaymentInfo,
            shouldRender: hasCardOnFileAccess(),
          },
          {
            label: 'Send Paylink',
            onClick: () => {
              setIsSendPaylinkModalOpen(true);
            },
            disabled:
              !canAccess(AuthPermissions.CREATE_PAYLINKS) &&
              !canAccess(AuthPermissions.CREATE_OWN_PAYLINKS),
          },
          {
            label: 'View past Paylinks',
            onClick: () => navigate(`${paylinkPaths.VIEW_ALL}?${viewPastPaylinkParams.toString()}`),
            disabled:
              !canAccess(AuthPermissions.READ_PAYLINKS) &&
              !canAccess(AuthPermissions.READ_OWN_PAYLINKS),
          },
          {
            label: 'Edit',
            onClick: () => {
              setIsEditModalOpen(true);
            },
            disabled:
              !canAccess(AuthPermissions.UPDATE_PAYLINK_CUSTOMERS) &&
              !canAccess(AuthPermissions.UPDATE_OWN_PAYLINK_CUSTOMERS),
          },
          {
            label: 'Delete',
            onClick: () => {
              setIsDeleteDialogOpen(true);
            },
            disabled:
              !canAccess(AuthPermissions.DELETE_PAYLINK_CUSTOMERS) &&
              !canAccess(AuthPermissions.DELETE_OWN_PAYLINK_CUSTOMERS),
          },
        ]}
      />
    </>
  );
};
